import { watch, type Ref } from 'vue'
import { syncObjects, applyRuntimePatches } from '@/three/ObjectManager'
import { computeLiveDataPatches } from '@/runtime/liveData/computeLiveDataPatches'
import { startMockLiveDataFeed } from '@/runtime/realtime/mockRealtimeFeed'
import type { SceneManager } from '@/three/SceneManager'
import type { ConnectionManager } from '@/three/ConnectionManager'
import { useSceneStore } from '@/stores/scene'
import type { RealtimeConnectionState } from '@/types/realtime'
import type { SceneData } from '@/types/scene'
import { mergeDeepLiveData } from '@/utils/liveDataPaths'

type SceneStore = ReturnType<typeof useSceneStore>

function effectiveMockIntervalMs(baseMs: number, scene: SceneData): number {
  const di = scene.dataIntegration
  // 优先使用直接设置的毫秒值
  if (di?.mockIntervalMs != null && Number.isFinite(di.mockIntervalMs)) {
    return Math.max(200, di.mockIntervalMs)
  }
  // 兼容旧 mockSpeed（0~1）
  const speed = di?.mockSpeed
  if (speed == null || Number.isNaN(speed)) return baseMs
  const s = Math.min(1, Math.max(0, speed))
  return Math.max(200, Math.round(baseMs * (2 - s)))
}

export interface RealtimePipelineDeps {
  getSm: () => SceneManager | undefined
  getConnectionManager: () => ConnectionManager | undefined
  sceneStore: SceneStore
  getCurrentFloorId: () => string
  runtimeConnectionState: Ref<RealtimeConnectionState>
  debugRealtimeEnabled: Ref<boolean>
  runtimeBadgeVisible: Ref<boolean>
  runtimeDataSource: Ref<'mock' | 'live' | 'static'>
  runtimeIsStale: Ref<boolean>
  lastSnapshotAt: Ref<number | null>
}

export function createRealtimePipeline(deps: RealtimePipelineDeps) {
  let stopMockRealtime: (() => void) | null = null
  /** 最近一次 Mock/实时下发的 liveData，用于测试值覆盖变更后立即重算 */
  let lastMockLiveData: Record<string, unknown> = {}

  function applyLiveDataPatches(liveData: Record<string, unknown>) {
    lastMockLiveData = liveData
    const scene = deps.sceneStore.activeScene()
    if (!scene) return
    const ov = deps.sceneStore.getPreviewLiveDataOverrides?.() ?? {}
    const merged =
      ov && typeof ov === 'object' && Object.keys(ov).length > 0
        ? mergeDeepLiveData(liveData, ov as Record<string, unknown>)
        : liveData
    deps.sceneStore.setEditorPreviewMergedLiveData(merged)
    const patches = computeLiveDataPatches(scene, merged)
    applyRuntimePatches(patches)
  }

  /** 无 Mock 时仅用「测试值」覆盖驱动绑定 */
  function refreshEditorPreviewLiveDataOnly() {
    applyLiveDataPatches({})
  }

  /** 预览态下修改测试值后，与当前 Mock 快照合并并立即应用 */
  function reapplyPreviewLiveDataWithOverrides() {
    applyLiveDataPatches(lastMockLiveData)
  }

  function stopRealtimePipeline() {
    lastMockLiveData = {}
    stopMockRealtime?.()
    stopMockRealtime = null
    deps.runtimeConnectionState.value = 'offline'
    deps.debugRealtimeEnabled.value = false
    deps.runtimeDataSource.value = 'mock'
    deps.runtimeIsStale.value = false
    deps.lastSnapshotAt.value = null
    deps.sceneStore.setEditorPreviewMergedLiveData({})

    const sm = deps.getSm()
    if (sm) {
      syncObjects(deps.sceneStore.activeObjects(), deps.getCurrentFloorId())
      deps.getConnectionManager()?.syncConnections(deps.sceneStore.activeConnections())
      sm.markDirty()
    }
  }

  /** 数据源=static：停 Mock、停轮询，仅由「测试值」驱动绑定（等价旧 mockEnabled=false） */
  function setupStaticPipeline() {
    stopRealtimePipeline()
    deps.runtimeDataSource.value = 'static'
    deps.runtimeConnectionState.value = 'idle'
    deps.debugRealtimeEnabled.value = false
    refreshEditorPreviewLiveDataOnly()
  }

  /** 数据源=mock：有最近真数则抖动真数；否则按绑定点位名造合理演示值 */
  function setupPreviewPipeline() {
    stopRealtimePipeline()
    const scene = deps.sceneStore.activeScene()
    if (!scene) return
    const rt = deps.sceneStore.getRealtimeDebugConfig()
    const preferPerformance = !!rt.preferPerformance
    deps.runtimeDataSource.value = 'mock'
    deps.runtimeConnectionState.value = 'connected'
    deps.debugRealtimeEnabled.value = !!rt.showBadge
    deps.runtimeBadgeVisible.value = rt.showBadge
    const interval = effectiveMockIntervalMs(
      Math.max(preferPerformance ? 1000 : 600, rt.batchMs),
      scene
    )
    const seed = deps.sceneStore.getLastLiveDataSeed?.() ?? {}
    stopMockRealtime = startMockLiveDataFeed(
      scene,
      (liveData) => {
        applyLiveDataPatches(liveData)
      },
      interval,
      { seedLiveData: seed },
    )
  }

  // 测试值变化时，无论当前处于何种模式都立即重算并驱动 3D 对象
  watch(
    () => deps.sceneStore.getPreviewLiveDataOverrides(),
    () => {
      if (deps.sceneStore.getDataSourceMode() === 'static') refreshEditorPreviewLiveDataOnly()
      else reapplyPreviewLiveDataWithOverrides()
    },
    { deep: true }
  )

  return {
    stopRealtimePipeline,
    setupStaticPipeline,
    setupPreviewPipeline,
    refreshEditorPreviewLiveDataOnly,
    reapplyPreviewLiveDataWithOverrides,
  }
}
